import ChevronXSIcon from "../../icons/chevron-lg.icon";
import CrossIcon from "../../icons/cross.icon";
import ExportIcon from "../../icons/export.icon";
import ShareIcon from "../../icons/share.icon";
import { Dropdown, Spinner } from "react-bootstrap";
import "./index.styles.scss";
import { useNavigate, useLocation } from "react-router-dom";
import { ROUTE_PATHS, APP_ROUTES, REACT_APP_DEVUI_URL } from "../../../constants/app.constants";
import PreviewReport from "../preview-report";
import CopyIcon from "../../icons/copy.icon";
import useDropdown from "../../../hooks/dropdown.hook";
import { CallToast } from "../../PriceCalculator/modals/CustomToast";
import useDownloadPDFState from "./download-pdf.state";
import { getAPIData, postData } from "../../../Utils/commonApi";
import { useAuth } from "../../context/Auth";
import { useCallback, useEffect, useRef, useState } from "react";
import SendIcon from "../../icons/send";
import stringUtils from "../../../Utils/string.utils";

const ActionButton = ({ Icon, label, disabled = false, onClick = () => { } }) => {
  return (
    <button
      className="action-button grey-button my-button f-grey f-14 line-height-22 f-500 d-flex align-items-center"
      onClick={onClick}
      disabled={disabled}
    >
      <span>{label}</span>
      <Icon />
    </button>
  );
};

const ShareDropdown = ({ mspId, reportId }) => {
  const { isOpen, refBtn, refMenu, setIsOpen } = useDropdown({});
  const { authTokens } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [linkURL, setLinkURL] = useState('');

  const onCopy = () => {
    navigator.clipboard.writeText(linkURL);
    setIsOpen(false);
    CallToast("Link copied to clipboard", false, false, "v2style");
  }

  const getSecurityStackReportShare = useCallback(async () => {
    if (linkURL) return;
    setIsLoading(true);
    await getAPIData(21, authTokens, {
      optionEnum1: mspId ?? 0,
      optionEnum2: reportId ?? 0
    }).then((response) => {
      if (response?.Success) {
        setLinkURL(REACT_APP_DEVUI_URL + APP_ROUTES.subscriptionPortalPages.secStackReportPreview + `?linkId=${encodeURIComponent(response?.Message)}`);
      } else {
        CallToast("Something went wrong", false, false, "v2style");
      }
    }).finally(() => {
      setIsLoading(false);
    });
  }, [authTokens, linkURL, mspId, reportId]);

  useEffect(() => {
    if (isOpen) getSecurityStackReportShare();
  }, [isOpen, getSecurityStackReportShare])

  return (
    <Dropdown className='share-dropdown transparennt-wrapper-dropdown my-dropdown my-dropdown-common' show={isOpen} onToggle={() => setIsOpen(prev => !prev)}>
      <Dropdown.Toggle ref={refBtn} >
        <button className="action-button grey-button my-button f-grey f-14 line-height-22 f-500 d-flex align-items-center">
          <span>Share</span>
          <ShareIcon />
        </button>
      </Dropdown.Toggle>
      <Dropdown.Menu ref={refMenu} className="menu">
        <div className="menu-header f-grey">
          <div className="menu-header-title f-14 line-height-22 f-400">Share public link</div>
          <div className="menu-header-close" onClick={() => setIsOpen(false)}>
            <CrossIcon width={9} height={9} />
          </div>
        </div>
        <div className="menu-content">
          <p className="m-0">Anyone with the link can view it. No login or password required.</p>
          <div className="link-container d-flex align-items-center gap-8 justify-content-between">
            <span className="text-ellipsis f-black">
              {isLoading ? 'Loading...' : linkURL}
            </span>
            <button className="grey-button my-button f-grey f-12 line-height-18 f-500 d-flex align-items-center" onClick={onCopy} disabled={isLoading}>
              <CopyIcon />
              <span>Copy</span>
            </button>
          </div>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

const ReportPreviewSharePage = ({ backPath = ROUTE_PATHS.subscriptionPortalPages.secStackReportReview }) => {
  const { authTokens } = useAuth();
  const location = useLocation();
  const { mspId, reportId } = location.state || {};

  const { downloadPDF, isDownloading } = useDownloadPDFState(mspId, reportId);
  const navigate = useNavigate();

  const onBack = () => {
    navigate(backPath, {
      state: {
        forView: true,
        mspId: mspId,
        passedReportId: reportId
      }
    });
  }

  const onExport = () => {
    downloadPDF();
  }

  async function sendByEmail(emails) {
    await postData(authTokens, "EmailSecurityStackReportLink", {
      accessKey: JSON.parse(localStorage.getItem("user_MessageObj")),
      SecurityStackReportId: reportId ?? '',
      MSPCustomerId: mspId ?? '',
      EmailAddress: emails ?? '',
    })
  }

  const ExportIconComp = isDownloading
    ? () => (
      <div className="d-flex align-items-center justify-content-center" style={{ width: '20px', height: '20px' }}>
        <Spinner animation="border" variant="secondary" size="sm" style={{ width: '16px', height: '16px' }} />
      </div>
    )
    : ExportIcon;


  return (
    <div className="report-preview-share-page">
      <div className="page-header">
        <div className="back-button" onClick={onBack}>
          <ChevronXSIcon />
        </div>
        <div className="export-share-print-buttons d-flex gap-8">
          <ActionButton label="Export" Icon={ExportIconComp} onClick={onExport} disabled={isDownloading} />
          <ShareDropdown mspId={mspId} reportId={reportId} />
          <SendByEmailDropdown onSend={sendByEmail} />
        </div>
        <div>
          <button
            className="action-button blue-button my-button f-white f-14 line-height-22 f-500 d-flex align-items-center"
            onClick={onBack}
          >
            <span>Close</span>
            <div className="cross-icon">
              <CrossIcon width={10} height={10} />
            </div>
          </button>
        </div>
      </div>
      <div className="page-content">
        <div className="report-wrapper">
          <PreviewReport offsetTop="38px" mspId={mspId} reportId={reportId} />
        </div>
      </div>
    </div>
  );
};

export default ReportPreviewSharePage;

const SendByEmailDropdown = ({ onSend = () => { } }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { isOpen, refBtn, refMenu, setIsOpen } = useDropdown({});
  const [emailTags, setEmailTags] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef(null);
  const users = [];

  const addEmailTokens = (rawValue) => {
    if (!rawValue) return 0;
    const parts = String(rawValue)
      .split(/[\s,;\n]+/)
      .map((p) => p.trim())
      .filter(Boolean);
    if (parts.length === 0) return 0;

    const existingLower = new Set(emailTags.map((e) => e.toLowerCase()));
    const toAdd = [];
    parts.forEach((p) => {
      if (stringUtils.isEmail(p) && !existingLower.has(p.toLowerCase())) {
        existingLower.add(p.toLowerCase());
        toAdd.push(p);
      }
    });
    if (toAdd.length) setEmailTags((prev) => [...prev, ...toAdd]);
    return toAdd.length;
  };

  const removeEmail = (emailToRemove) => {
    setEmailTags((prev) => prev.filter((e) => e !== emailToRemove));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Tab' || e.key === ',' || e.key === ' ') {
      if (inputValue.trim()) {
        e.preventDefault();
        const added = addEmailTokens(inputValue);
        if (added > 0) {
          setInputValue("");
        }
      }
    } else if (e.key === 'Backspace' && !inputValue && emailTags.length) {
      e.preventDefault();
      setEmailTags((prev) => prev.slice(0, prev.length - 1));
    }
  };

  const handleChange = (e) => {
    const val = e?.target?.value || "";
    if (val.includes(',')) {
      const segments = val.split(',');
      addEmailTokens(segments.slice(0, -1).join(','));
      const last = segments[segments.length - 1];
      setInputValue(last);
    } else {
      setInputValue(val);
    }
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData?.getData('text') || '';
    if (/[\s,;\n]/.test(paste)) {
      e.preventDefault();
      const added = addEmailTokens(paste);
      if (added > 0) setInputValue('');
    }
  };

  const handleSend = () => {
    const pending = inputValue.trim();
    const finalEmails = [...emailTags];
    if (pending) {
      if (stringUtils.isEmail(pending) && !finalEmails.map((e) => e.toLowerCase()).includes(pending.toLowerCase())) {
        finalEmails.push(pending);
      }
    }
    if (finalEmails.length === 0) return;

    setIsLoading(true);
    onSend(finalEmails.join(','))
      .then(() => {
        setIsOpen(false);
        setEmailTags([]);
        setInputValue('');
        CallToast("Email sent successfully", false, false, "v2style");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const sendDisabled = isLoading || (!inputValue.trim() && emailTags.length === 0);

  return (
    <Dropdown className='share-dropdown transparennt-wrapper-dropdown my-dropdown my-dropdown-common' show={isOpen} onToggle={() => setIsOpen(prev => !prev)}>
      <Dropdown.Toggle ref={refBtn} >
        <button className="action-button grey-button my-button f-grey f-14 line-height-22 f-500 d-flex align-items-center">
          <span>Share by Email</span>
          <SendIcon />
        </button>
      </Dropdown.Toggle>
      <Dropdown.Menu ref={refMenu} className="menu">
        <div className="menu-header f-grey">
          <div className="menu-header-title f-14 line-height-22 f-400">Share by Email</div>
          <div className="menu-header-close" onClick={() => setIsOpen(false)}>
            <CrossIcon width={9} height={9} />
          </div>
        </div>
        <div className="p-3">
          <div className="d-flex align-items-center">
            <div
              className="highlight-input-with-grey-border bg-transparent p-2 w-100 mr-2 d-flex align-items-center flex-wrap gap-4"
              onClick={() => inputRef.current?.focus()}
            >
              {emailTags.map((email) => (
                <span key={email} className="d-inline-flex align-items-center bg-grey f-grey radius-4" style={{ padding: '2px 6px' }}>
                  <span className="mr-1 f-12">{email}</span>
                  <button
                    type="button"
                    onClick={() => removeEmail(email)}
                    className="my-button"
                    aria-label={`Remove ${email}`}
                  >
                    <CrossIcon width={9} height={9} />
                  </button>
                </span>
              ))}
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                placeholder={emailTags.length ? '' : 'Type emails and press comma, Enter or Tab'}
                className="w-100"
              />
            </div>
            <button className="blue-button my-button f-grey f-12 line-height-18 f-500 d-flex align-items-center gap-4" onClick={handleSend} disabled={sendDisabled}>
              {isLoading && (
                <Spinner
                  animation="border"
                  variant="grey"
                  size="sm"
                  style={{ width: '12px', height: '12px' }}
                />
              )}
              <span>
                {isLoading ? 'Sending...' : 'Send'}
              </span>
            </button>
          </div>

          {users?.length > 0 && (
            <>
              <div className="f-500 f-14 f-black line-height-22 my-2">People with a copy</div>
              {users?.map((user) => (
                <UserItem name={user?.name} email={user.email} icon={user?.icon} />
              ))}
            </>
          )}
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

const UserItem = ({ name, email, icon }) => (
  <div className="d-flex align-items-start mb-2">
    <img
      alt=""
      src={icon ?? "/images/user-logo.svg"}
      width={16} height={16}
      className="rounded-full mr-2 mt-1"
      onError={({ currentTarget }) => {
        currentTarget.onerror = null;
        currentTarget.src = "/images/user-logo.svg";
      }}
    />
    <div>
      <div className="f-400 f-14 f-black line-height-22">{name}</div>
      <div className={`${name ? 'f-400 f-14 f-grey' : 'f-400 f-14 f-black'} line-height-22`}>{email}</div>
    </div>
  </div>
)